import { datadogRum } from '@datadog/browser-rum';
import React, { useCallback, useContext, useEffect } from 'react';

import type { Environment } from '#src/config';

interface DatadogContext {
  setDatadogUser: (id: string) => void;
}

const ctx = React.createContext<DatadogContext>({
  setDatadogUser: () => {},
});

interface DatadogProps {
  children: React.JSX.Element;
  environment: Environment;
  version: string;
}

export const DatadogProvider = ({
  children,
  environment,
  version,
}: DatadogProps) => {
  useEffect(() => {
    if (environment === 'prod') {
      // https://docs.datadoghq.com/real_user_monitoring/browser/#configuration
      datadogRum.init({
        applicationId: 'fed3deb4-d116-44e4-b4b9-be54b74f312b',
        clientToken: 'pub870a10659f468170f11cea9bf021313c',
        site: 'datadoghq.com',
        service: 'ie-job-ad-preview-ui',
        env: environment,
        version,

        // Only sample 1% of all users to minimise session usage
        sessionSampleRate: 1,
        // Of the 1% of sampled sessions, enable session replay
        sessionReplaySampleRate: 100,
        silentMultipleInit: true,
        defaultPrivacyLevel: 'mask',
        // datadog/browser-sdk#1578
        workerUrl: new URL(
          '../../../node_modules/@datadog/browser-worker/bundle/worker.js',
          import.meta.url,
        ).href,
      });
      datadogRum.setGlobalContextProperty('team', 'indirect-experience');
    }
  }, [environment, version]);

  const setDatadogUser = useCallback(
    (id: string) => {
      if (environment === 'prod') {
        datadogRum.setUser({ id });
      }
    },
    [environment],
  );

  return <ctx.Provider value={{ setDatadogUser }}>{children}</ctx.Provider>;
};

export const useDatadog = () => useContext(ctx);
